import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { FaCloudUploadAlt, FaCog, FaRegTrashAlt, FaTimes } from 'react-icons/fa';
import utilitiesAPI from '../api/utilitiesAPI';
import { useUtilitiesCompany } from '../context/UtilitiesCompanyContext';
import '../styles/utilitiesFormOdoo.css';
import {
  RESOURCES,
  emptyLineFor,
  emptyValuesFor,
  lookupTypesFor,
  resourceAllowedForCompany,
} from '../utils/utilitiesResources';
import { formatDate, formatMoney, isUnauthorized, many2oneId, many2oneName, pickRecords } from '../utils/utilitiesFormat';

const LOOKUP_TYPES = ['lookup', 'lookupMulti', 'depositInvoice', 'childBank', 'outstandingBill'];
const DROPDOWN_LIMIT = 7;

function isBlank(v) {
  return v === '' || v === null || v === undefined || v === false || (Array.isArray(v) && v.length === 0);
}

function valueFromRecord(field, raw) {
  if (field.type === 'lookupMulti') return Array.isArray(raw) ? raw.map(many2oneId).filter(Boolean) : [];
  if (field.type === 'displayMany') return Array.isArray(raw) ? raw.map(many2oneName).join(', ') : '';
  if (field.type === 'display') return many2oneName(raw);
  if (field.type === 'boolean') return Boolean(raw);
  if (LOOKUP_TYPES.includes(field.type)) return many2oneId(raw) || '';
  return raw ?? '';
}

function isVisible(field, values) {
  return !field.visibleIf || field.visibleIf(values);
}

function isRequired(field, values) {
  if (field.readonly || !isVisible(field, values)) return false;
  return Boolean(field.required || field.requiredIf?.(values));
}

function buildValues(resource, values, creating) {
  const out = {};
  resource.fields
    .filter((f) => !f.readonly)
    .forEach((f) => {
      const v = values[f.name];
      if (f.type === 'boolean') {
        out[f.name] = Boolean(v);
        return;
      }
      if (isBlank(v)) {
        // Clearing a field on an existing draft must be sent explicitly,
        // unless blank means the backend's default (blankText).
        if (!creating && !f.blankText) out[f.name] = f.type === 'lookupMulti' ? [] : false;
        return;
      }
      if (LOOKUP_TYPES.includes(f.type)) out[f.name] = f.type === 'lookupMulti' ? v.map(Number) : Number(v);
      else if (f.type === 'number') out[f.name] = Number(v);
      else out[f.name] = v;
    });
  return out;
}

function editableColumns(resource) {
  return resource.lines.columns.filter((c) => !c.readonly);
}

function buildLines(resource, lines) {
  return lines.map((line) => {
    const out = {};
    editableColumns(resource).forEach((c) => {
      const v = line[c.name];
      if (isBlank(v)) return;
      if (c.type === 'lookupMulti') out[c.name] = v.map(Number);
      else if (c.type === 'number' || c.type === 'lookup' || c.type === 'outstandingBill') out[c.name] = Number(v);
      else out[c.name] = v;
    });
    return out;
  });
}

/** Returns the invalid field and line-cell names, and what to tell the user. */
function validate(resource, values, lines) {
  const fields = new Set(resource.fields.filter((f) => isRequired(f, values) && isBlank(values[f.name])).map((f) => f.name));
  const cells = new Set();
  if (resource.lines) {
    lines.forEach((line, i) => {
      editableColumns(resource)
        .filter((c) => c.required && isBlank(line[c.name]))
        .forEach((c) => cells.add(`${i}:${c.name}`));
    });
  }
  const noLines = Boolean(resource.lines) && lines.length === 0;
  if (!fields.size && !cells.size && !noLines) return null;
  const message = noLines && !fields.size && !cells.size ? `Add a line to ${resource.lines.label}.` : 'Missing required fields';
  return { fields, cells, message };
}

function optionLabel(option, field) {
  if (!option) return '';
  if (option.display_name) return option.display_name;
  if (field?.lookup?.endsWith('_accounts') && option.code) return `${option.code} ${option.name}`;
  return option.name || option.label || option.code || String(option.id ?? '');
}

function optionsFor(field, { lookups, invoices, childBanks, outstanding }) {
  if (field.type === 'depositInvoice') return invoices;
  if (field.type === 'childBank') return childBanks;
  if (field.type === 'outstandingBill') return outstanding;
  return lookups[field.lookup] || [];
}

/** Odoo's many2one input: type to filter, pick from a short list, "Search More..." for the rest. */
function Autocomplete({ id, text, options, labelOf, onPick, onClear, placeholder, invalid }) {
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const [query, setQuery] = useState(null);
  const [open, setOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [active, setActive] = useState(0);
  const [box, setBox] = useState(null);

  const matches = useMemo(() => {
    if (!open) return [];
    const q = (query ?? '').trim().toLowerCase();
    return q ? options.filter((o) => labelOf(o).toLowerCase().includes(q)) : options;
  }, [open, query, options, labelOf]);
  const visible = showAll ? matches.slice(0, 200) : matches.slice(0, DROPDOWN_LIMIT);
  const hasMore = !showAll && matches.length > DROPDOWN_LIMIT;

  useEffect(() => {
    if (!open) return undefined;
    const place = () => {
      const r = inputRef.current?.getBoundingClientRect();
      if (r) setBox({ left: r.left, top: r.bottom, width: Math.max(r.width, 240) });
    };
    place();
    const close = (e) => {
      if (listRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', place);
    };
  }, [open]);

  const openList = () => {
    setActive(0);
    setShowAll(false);
    setOpen(true);
  };

  const pick = (option) => {
    onPick(option);
    setQuery(null);
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) openList();
      else setActive((a) => Math.min(a + 1, visible.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (open && visible[active]) pick(visible[active]);
    } else if (e.key === 'Escape') {
      setOpen(false);
      setQuery(null);
    }
  };

  return (
    <div className="o-m2o">
      <input
        ref={inputRef}
        id={id}
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        autoComplete="off"
        className={`o-input${invalid ? ' o-input--invalid' : ''}`}
        placeholder={placeholder}
        value={query ?? text ?? ''}
        onChange={(e) => {
          setQuery(e.target.value);
          setActive(0);
          setShowAll(false);
          setOpen(true);
        }}
        onClick={() => !open && openList()}
        onKeyDown={onKeyDown}
        onBlur={() => {
          if (query === '' && onClear) onClear();
          setQuery(null);
          setOpen(false);
        }}
      />
      <span className="o-caret" />
      {open &&
        box &&
        createPortal(
          <ul
            ref={listRef}
            className="o-utilities-dropdown"
            role="listbox"
            style={{ left: box.left, top: box.top, width: box.width }}
            onMouseDown={(e) => e.preventDefault()}
          >
            {visible.map((o, i) => (
              <li
                key={o.id}
                role="option"
                aria-selected={i === active}
                className={i === active ? 'is-active' : ''}
                title={labelOf(o)}
                onMouseEnter={() => setActive(i)}
                onClick={() => pick(o)}
              >
                {labelOf(o)}
              </li>
            ))}
            {visible.length === 0 && <li className="is-empty">No records</li>}
            {hasMore && (
              <li className="is-more" onClick={() => setShowAll(true)}>
                Search More...
              </li>
            )}
          </ul>,
          document.body
        )}
    </div>
  );
}

function DateControl({ id, value, onChange, invalid }) {
  const nativeRef = useRef(null);
  const openPicker = () => {
    try {
      nativeRef.current?.showPicker();
    } catch {
      nativeRef.current?.focus();
    }
  };
  return (
    <div className="o-date">
      <button
        id={id}
        type="button"
        className={`o-input${invalid ? ' o-input--invalid' : ''}`}
        onClick={openPicker}
      >
        {formatDate(value) || ' '}
      </button>
      <input ref={nativeRef} type="date" tabIndex={-1} aria-hidden="true" value={value || ''} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

function readonlyText(field, value, options, fallback) {
  if (field.type === 'select') return field.options.find((o) => o.value === value)?.label || '';
  if (field.type === 'date') return formatDate(value);
  if (field.type === 'money' || field.type === 'number') return value === '' || value == null ? '' : formatMoney(value);
  if (field.type === 'lookupMulti') {
    return (Array.isArray(value) ? value : [])
      .map((id) => optionLabel(options.find((o) => String(o.id) === String(id)), field) || String(id))
      .join(', ');
  }
  if (LOOKUP_TYPES.includes(field.type)) {
    const match = options.find((o) => String(o.id) === String(value));
    return match ? optionLabel(match, field) : fallback || '';
  }
  return value ?? '';
}

function OdooControl({ id, field, value, onChange, onPickOption, editable, options = [], fallback = '', invalid = false }) {
  const labelOf = useCallback((o) => optionLabel(o, field), [field]);

  if (field.type === 'boolean') {
    return (
      <input
        id={id}
        type="checkbox"
        className="o-checkbox"
        checked={Boolean(value)}
        disabled={!editable}
        onChange={(e) => onChange?.(e.target.checked)}
      />
    );
  }
  if (!editable || field.readonly) {
    return <div className="o-readonly">{readonlyText(field, value, options, fallback)}</div>;
  }

  const inputClass = `o-input${invalid ? ' o-input--invalid' : ''}`;

  if (field.type === 'textarea') {
    return (
      <textarea
        id={id}
        className={inputClass}
        rows={1}
        placeholder={field.placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }
  if (field.type === 'select') {
    return (
      <div className="o-select-wrap">
        <select id={id} className={inputClass} value={value} onChange={(e) => onChange(e.target.value)}>
          {!field.required && <option value="" />}
          {field.options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <span className="o-caret" />
      </div>
    );
  }
  if (field.type === 'date') {
    return <DateControl id={id} value={value} onChange={onChange} invalid={invalid} />;
  }
  if (field.type === 'number' || field.type === 'text') {
    return (
      <input
        id={id}
        type={field.type}
        step={field.type === 'number' ? '0.01' : undefined}
        className={inputClass}
        placeholder={field.placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }
  if (field.type === 'lookupMulti') {
    const selected = (Array.isArray(value) ? value : []).map(Number);
    const remaining = options.filter((o) => !selected.includes(Number(o.id)));
    return (
      <div className="o-tags">
        {selected.map((tagId) => (
          <span key={tagId} className="o-tag">
            {labelOf(options.find((o) => Number(o.id) === tagId)) || tagId}
            <button type="button" aria-label="Remove" onClick={() => onChange(selected.filter((v) => v !== tagId))}>
              <FaTimes size={9} />
            </button>
          </span>
        ))}
        <Autocomplete
          id={id}
          text=""
          options={remaining}
          labelOf={labelOf}
          placeholder={selected.length ? '' : field.placeholder}
          onPick={(o) => onChange([...selected, Number(o.id)])}
        />
      </div>
    );
  }
  const match = options.find((o) => String(o.id) === String(value));
  return (
    <Autocomplete
      id={id}
      text={match ? labelOf(match) : value ? fallback : field.blankText || ''}
      options={options}
      labelOf={labelOf}
      placeholder={field.placeholder}
      invalid={invalid}
      onPick={(o) => (onPickOption ? onPickOption(o) : onChange(o.id))}
      onClear={() => onChange('')}
    />
  );
}

export default function UtilitiesFormPage() {
  const { resource: resourceKey, id } = useParams();
  const navigate = useNavigate();
  const resource = RESOURCES[resourceKey];
  const { companies, company, companyId, setCompanyId } = useUtilitiesCompany();
  const isNew = id === 'new' || !id;

  const [lookups, setLookups] = useState({});
  const [invoices, setInvoices] = useState([]);
  const [childBanks, setChildBanks] = useState([]);
  const [outstanding, setOutstanding] = useState([]);
  const [values, setValues] = useState({});
  const [lines, setLines] = useState([]);
  const [recordName, setRecordName] = useState('');
  const [meta, setMeta] = useState({ state: 'draft', can_edit: true, can_post: true, can_delete: true });
  const [partner, setPartner] = useState(null);
  const [tab, setTab] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [invalid, setInvalid] = useState({ fields: new Set(), cells: new Set() });
  const [menuOpen, setMenuOpen] = useState(false);
  const [toast, setToast] = useState(null);

  const clearInvalid = () => setInvalid({ fields: new Set(), cells: new Set() });

  const handleError = useCallback(
    (err) => {
      if (isUnauthorized(err)) return navigate('/bu-dashboard/login', { replace: true });
      setToast({ message: err.message, sticky: true });
      return null;
    },
    [navigate]
  );

  useEffect(() => {
    if (!toast || toast.sticky) return undefined;
    const timer = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  const applyRecord = useCallback(
    (raw) => {
      const rec = raw?.record || raw?.data || raw || {};
      const nextValues = {};
      resource.fields.forEach((f) => {
        nextValues[f.name] = valueFromRecord(f, rec[f.name]);
        // Keep the name of a many2one so it still shows if the lookup list lacks it.
        if (LOOKUP_TYPES.includes(f.type) && f.type !== 'lookupMulti' && rec[f.name] && typeof rec[f.name] === 'object') {
          nextValues[`${f.name}__label`] = many2oneName(rec[f.name]);
        }
      });
      setValues(nextValues);
      if (resource.lines) {
        setLines(
          (rec.lines || []).map((line) => {
            const row = emptyLineFor(resource);
            resource.lines.columns.forEach((c) => {
              row[c.name] = valueFromRecord(c, line[c.name]);
              if (LOOKUP_TYPES.includes(c.type) && c.type !== 'lookupMulti' && line[c.name] && typeof line[c.name] === 'object') {
                row[`${c.name}__label`] = many2oneName(line[c.name]);
              }
            });
            return row;
          })
        );
      }
      setRecordName(rec.name || '');
      setMeta({
        state: rec.state || 'draft',
        can_edit: rec.can_edit ?? true,
        can_post: rec.can_post ?? true,
        can_delete: rec.can_delete ?? true,
      });
      setDirty(false);
      setInvalid({ fields: new Set(), cells: new Set() });
    },
    [resource]
  );

  // Main load: lookups for the dropdowns, plus the record when editing.
  useEffect(() => {
    if (!resource || !companyId) return undefined;
    let cancelled = false;
    setLoading(true);
    setRecordName('');
    setDirty(false);
    setMenuOpen(false);
    setInvalid({ fields: new Set(), cells: new Set() });
    setValues(isNew ? emptyValuesFor(resource) : {});
    setLines([]);

    const types = lookupTypesFor(resource);
    Promise.all([
      types.length ? utilitiesAPI.lookups(companyId, types) : Promise.resolve({}),
      isNew ? Promise.resolve(null) : utilitiesAPI.get(resource.key, companyId, id),
    ])
      .then(([lk, rec]) => {
        if (cancelled) return;
        setLookups(lk?.lookups || lk || {});
        if (rec) {
          applyRecord(rec);
          return;
        }
        setMeta({ state: 'draft', can_edit: true, can_post: true, can_delete: true });
      })
      .catch((err) => !cancelled && handleError(err))
      .finally(() => !cancelled && setLoading(false));

    return () => {
      cancelled = true;
    };
  }, [resource, companyId, id, isNew, applyRecord, handleError]);

  // Deposits: request invoices come from the parent company.
  useEffect(() => {
    if (resource?.key !== 'deposits' || !companyId) return undefined;
    let cancelled = false;
    utilitiesAPI
      .depositInvoices(companyId)
      .then((res) => !cancelled && setInvoices(pickRecords(res, 'invoices')))
      .catch((err) => !cancelled && handleError(err));
    return () => {
      cancelled = true;
    };
  }, [resource, companyId, handleError]);

  // Deposits: the child company's bank journals depend on the chosen invoice.
  const invoiceId = values.invoice_id;
  const canEditRecord = isNew || meta.can_edit;
  useEffect(() => {
    if (resource?.key !== 'deposits' || !companyId || !invoiceId || !canEditRecord) {
      setChildBanks([]);
      return undefined;
    }
    let cancelled = false;
    utilitiesAPI
      .childBanks(companyId, invoiceId)
      .then((res) => !cancelled && setChildBanks(pickRecords(res, 'banks')))
      .catch((err) => !cancelled && handleError(err));
    return () => {
      cancelled = true;
    };
  }, [resource, companyId, invoiceId, canEditRecord, handleError]);

  // Batch payments: pick from posted, unpaid vendor bills.
  const needsBills = Boolean(resource?.lines?.columns.some((c) => c.type === 'outstandingBill'));
  useEffect(() => {
    if (!needsBills || !companyId) return undefined;
    let cancelled = false;
    utilitiesAPI
      .outstandingBills(companyId)
      .then((res) => !cancelled && setOutstanding(pickRecords(res, 'bills')))
      .catch((err) => !cancelled && handleError(err));
    return () => {
      cancelled = true;
    };
  }, [needsBills, companyId, handleError]);

  // Bills and single payments: the vendor's balance follows the chosen vendor.
  const partnerId = resource?.key === 'bills' ? values.supplier_id : resource?.key === 'single-payments' ? values.vendor_id : null;
  useEffect(() => {
    if (!partnerId || !companyId || !canEditRecord) {
      setPartner(null);
      return undefined;
    }
    let cancelled = false;
    utilitiesAPI
      .partnerBalance(companyId, partnerId)
      .then((res) => !cancelled && setPartner(res))
      .catch(() => !cancelled && setPartner(null));
    return () => {
      cancelled = true;
    };
  }, [companyId, partnerId, canEditRecord]);

  if (!resource) return <Navigate to="/bu-dashboard/utilities/dashboard" replace />;

  const notAllowed = Boolean(company && !resourceAllowedForCompany(resource, company));
  const editable = !notAllowed && canEditRecord;
  const isDraft = isNew || meta.state === 'draft';
  const currentState = isNew ? 'draft' : meta.state;
  const optionCtx = { lookups, invoices, childBanks, outstanding };
  const fieldByName = Object.fromEntries(resource.fields.map((f) => [f.name, f]));
  const tabs = resource.tabs || [];
  const activeTab = tabs.some((t) => t.key === tab) ? tab : tabs[0]?.key;

  // Fields Odoo computes on the server, kept live while the draft is being edited.
  const liveValues = editable
    ? {
        total_amount: lines.reduce((sum, row) => sum + (Number(row.amount) || 0), 0),
        previous_balance: partnerId ? partner?.previous_balance ?? values.previous_balance ?? 0 : 0,
        vendor_bank_account_id: partnerId ? partner?.bank_account?.acc_number || values.vendor_bank_account_id || '' : '',
      }
    : {};

  const setValue = (name, value) => {
    setDirty(true);
    setInvalid((cur) => {
      const fields = new Set(cur.fields);
      fields.delete(name);
      return { ...cur, fields };
    });
    setValues((v) => {
      const next = { ...v, [name]: value };
      // Deposit: choosing an invoice fills the same fields as the Odoo form's onchange.
      if (resource.key === 'deposits' && name === 'invoice_id') {
        const invoice = invoices.find((i) => String(i.id) === String(value));
        next.customer_id = invoice?.company?.name || '';
        next.amount = invoice ? invoice.amount_residual : '';
        next.child_bank_journal_id = invoice?.receiving_bank_journal_id || '';
      }
      return next;
    });
  };

  const setLine = (index, patch) => {
    setDirty(true);
    setInvalid((cur) => {
      const cells = new Set(cur.cells);
      Object.keys(patch).forEach((name) => cells.delete(`${index}:${name}`));
      return { ...cur, cells };
    });
    setLines((rows) => rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  // Batch: choosing a bill fills its details and open amount, like the line's onchange.
  const pickBill = (index, bill) => {
    // display_name is "BILL/2026/11/0002 (REF) - Description".
    setLine(index, {
      bill_id: bill.id,
      bill_name: bill.name || '',
      bill_description: (bill.display_name || '').split(' - ').slice(1).join(' - '),
      bill_date: bill.invoice_date || '',
      vendor_id: bill.partner?.name || '',
      amount: bill.amount_residual,
    });
  };

  const addLine = () => {
    setDirty(true);
    setLines((rows) => [...rows, emptyLineFor(resource)]);
  };
  const removeLine = (index) => {
    setDirty(true);
    clearInvalid();
    setLines((rows) => rows.filter((_, i) => i !== index));
  };

  const goToList = () => navigate(`/bu-dashboard/utilities/${resource.key}`);
  const goToNew = () => navigate(`/bu-dashboard/utilities/${resource.key}/new`);

  /** Saves the form. Returns true when it worked, so callers can move on. */
  const save = async ({ post = false } = {}) => {
    const problem = validate(resource, values, lines);
    if (problem) {
      setInvalid({ fields: problem.fields, cells: problem.cells });
      setToast({ message: problem.message, sticky: false });
      return false;
    }
    setSaving(true);
    setToast(null);
    clearInvalid();
    const payloadValues = buildValues(resource, values, isNew);
    const payloadLines = resource.lines ? buildLines(resource, lines) : undefined;
    try {
      if (isNew) {
        const res = await utilitiesAPI.create(resource.key, companyId, {
          values: payloadValues,
          lines: payloadLines,
          post,
        });
        const newId = res?.id ?? res?.record?.id ?? res?.data?.id;
        setDirty(false);
        if (newId) navigate(`/bu-dashboard/utilities/${resource.key}/${newId}`, { replace: true });
      } else {
        await utilitiesAPI.update(resource.key, companyId, id, { values: payloadValues, lines: payloadLines });
        if (post) await utilitiesAPI.post(resource.key, companyId, id);
        applyRecord(await utilitiesAPI.get(resource.key, companyId, id));
      }
      return true;
    } catch (err) {
      handleError(err);
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Leaving a form with changes saves it first; a failed save keeps the user here.
  const leaveTo = async (go) => {
    if (dirty && editable && !(await save())) return;
    go();
  };

  // Discard reloads the saved record; on a new form it leaves the form.
  const discard = async () => {
    if (isNew) return goToList();
    setToast(null);
    setLoading(true);
    try {
      applyRecord(await utilitiesAPI.get(resource.key, companyId, id));
    } catch (err) {
      handleError(err);
    } finally {
      setLoading(false);
    }
    return undefined;
  };

  const removeDraft = async () => {
    setMenuOpen(false);
    if (!window.confirm('Are you sure you want to delete this record?')) return;
    setSaving(true);
    try {
      await utilitiesAPI.remove(resource.key, companyId, id);
      goToList();
    } catch (err) {
      handleError(err);
      setSaving(false);
    }
  };

  const fieldValue = (f) => {
    if (f.name in liveValues) return liveValues[f.name];
    return values[f.name] ?? (f.type === 'boolean' ? false : '');
  };

  const renderControl = (f, fieldId) => (
    <OdooControl
      id={fieldId}
      field={f}
      editable={editable}
      value={fieldValue(f)}
      options={optionsFor(f, optionCtx)}
      fallback={values[`${f.name}__label`]}
      invalid={invalid.fields.has(f.name)}
      onChange={(v) => setValue(f.name, v)}
    />
  );

  const renderField = (name) => {
    const f = fieldByName[name];
    if (!f || !isVisible(f, values) || (f.postedOnly && isDraft)) return null;
    const fieldId = `o-${resource.key}-${f.name}`;
    if (f.noLabel) {
      return (
        <div key={name} className="o-wrap-full">
          {renderControl(f, fieldId)}
        </div>
      );
    }
    const labelClass = [
      'o-label',
      f.readonly || !editable ? 'o-label--readonly' : '',
      invalid.fields.has(name) ? 'o-label--invalid' : '',
    ]
      .filter(Boolean)
      .join(' ');
    return (
      <React.Fragment key={name}>
        <label className={labelClass} htmlFor={fieldId}>
          {f.label}
          {f.help && (
            <sup className="o-help" title={f.help}>
              ?
            </sup>
          )}
        </label>
        <div className="o-wrap-input">{renderControl(f, fieldId)}</div>
      </React.Fragment>
    );
  };

  const renderGroup = (groups) => (
    <div className="o-group">
      {groups.map((names) => {
        const rendered = names.map(renderField).filter(Boolean);
        return (
          <div key={names.join()} className="o-inner-group">
            {rendered}
          </div>
        );
      })}
    </div>
  );

  const renderLines = () => {
    const columns = resource.lines.columns;
    // Odoo keeps at least four rows' height in an editable list.
    const fillers = Math.max(0, 4 - lines.length - (editable ? 1 : 0));
    return (
      <div className="o-list-wrap">
        <table className="o-list">
          <thead>
            <tr>
              {columns.map((c) => (
                <th key={c.name} className={c.type === 'number' ? 'o-num' : undefined} style={{ width: c.width, textAlign: c.type === 'number' ? 'right' : 'left' }}>
                  {c.label}
                </th>
              ))}
              {editable && <th style={{ width: 32 }} aria-label="Actions" />}
            </tr>
          </thead>
          <tbody>
            {lines.map((row, i) => (
              <tr key={i} className="o-list__row">
                {columns.map((c) => (
                  <td
                    key={c.name}
                    className={[c.type === 'number' ? 'o-num' : '', invalid.cells.has(`${i}:${c.name}`) ? 'o-list__cell-invalid' : ''].join(' ').trim() || undefined}
                  >
                    <OdooControl
                      id={`o-${resource.key}-line${i}-${c.name}`}
                      field={c}
                      value={row[c.name] ?? (c.type === 'lookupMulti' ? [] : '')}
                      editable={editable}
                      options={optionsFor(c, optionCtx)}
                      fallback={row[`${c.name}__label`]}
                      invalid={invalid.cells.has(`${i}:${c.name}`)}
                      onChange={(v) => setLine(i, { [c.name]: v })}
                      onPickOption={c.type === 'outstandingBill' ? (bill) => pickBill(i, bill) : undefined}
                    />
                  </td>
                ))}
                {editable && (
                  <td className="o-list__delete-cell">
                    <button type="button" className="o-list__delete" aria-label="Remove line" onClick={() => removeLine(i)}>
                      <FaRegTrashAlt />
                    </button>
                  </td>
                )}
              </tr>
            ))}
            {editable && (
              <tr>
                <td colSpan={columns.length + 1}>
                  <button type="button" className="o-list__add" onClick={addLine}>
                    Add a line
                  </button>
                </td>
              </tr>
            )}
            {Array.from({ length: fillers }, (_, i) => (
              <tr key={`filler-${i}`} aria-hidden="true">
                <td colSpan={columns.length + (editable ? 1 : 0)} />
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="o-utilities-form">
      <div className="o-control-panel">
        <div className="o-cp-left">
          {!notAllowed && (
            <button type="button" className="o-btn o-btn--outline" disabled={saving} onClick={() => leaveTo(goToNew)}>
              New
            </button>
          )}
          <nav className="o-breadcrumb" aria-label="Breadcrumb">
            <button type="button" className="o-breadcrumb__link" onClick={() => leaveTo(goToList)}>
              {resource.formTitle}
            </button>
            <div className="o-breadcrumb__row">
              <span className="o-breadcrumb__current">{isNew ? 'New' : recordName || `#${id}`}</span>
              {!isNew && !notAllowed && meta.can_delete && (
                <span className="o-menu">
                  <button
                    type="button"
                    className="o-icon-btn"
                    aria-label="Actions menu"
                    aria-expanded={menuOpen}
                    onClick={() => setMenuOpen((m) => !m)}
                  >
                    <FaCog />
                  </button>
                  {menuOpen && (
                    <div className="o-menu__list" role="menu">
                      <button type="button" role="menuitem" className="o-menu__item" onClick={removeDraft}>
                        Delete
                      </button>
                    </div>
                  )}
                </span>
              )}
              {editable && (isNew || dirty) && (
                <>
                  <button
                    type="button"
                    className="o-icon-btn"
                    aria-label="Save manually"
                    title="Save manually"
                    disabled={saving}
                    onClick={() => save()}
                  >
                    <FaCloudUploadAlt />
                  </button>
                  <button
                    type="button"
                    className="o-icon-btn"
                    aria-label="Discard all changes"
                    title="Discard all changes"
                    disabled={saving || loading}
                    onClick={discard}
                  >
                    <FaTimes />
                  </button>
                </>
              )}
            </div>
          </nav>
        </div>
        {companies.length > 1 && (
          <select
            className="o-company-select"
            aria-label="Company"
            value={companyId ?? ''}
            onChange={(e) => setCompanyId(e.target.value)}
          >
            {companies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        )}
      </div>

      {toast && (
        <div className="o-toast" role="alert">
          <span>{toast.message}</span>
          <button type="button" aria-label="Close" onClick={() => setToast(null)}>
            <FaTimes />
          </button>
        </div>
      )}

      {notAllowed ? (
        <div className="o-alert" role="status">
          {resource.formTitle} is not available in {company.name}. Please switch company.
        </div>
      ) : (
        <>
          <div className="o-statusbar">
            <div className="o-statusbar__buttons">
              {isDraft && meta.can_post && (
                <button type="button" className="o-btn o-btn--primary" disabled={saving || loading} onClick={() => save({ post: true })}>
                  {resource.postLabel}
                </button>
              )}
            </div>
            <div className="o-status" role="list" aria-label="Statusbar">
              {resource.states.map((s) => (
                <div
                  key={s.value}
                  role="listitem"
                  aria-current={s.value === currentState ? 'step' : undefined}
                  className={`o-status__step${s.value === currentState ? ' o-status__step--current' : ''}`}
                >
                  <span>{s.label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="o-sheet">
            {loading ? (
              <p className="o-sheet__loading">Loading…</p>
            ) : (
              <form onSubmit={(e) => e.preventDefault()}>
                {renderGroup(resource.groups)}
                {resource.afterGroups && renderGroup(resource.afterGroups)}

                {tabs.length > 0 && (
                  <div className="o-notebook">
                    <div className="o-notebook__tabs" role="tablist">
                      {tabs.map((t) => (
                        <button
                          key={t.key}
                          type="button"
                          role="tab"
                          aria-selected={t.key === activeTab}
                          className={`o-notebook__tab${t.key === activeTab ? ' o-notebook__tab--active' : ''}`}
                          onClick={() => setTab(t.key)}
                        >
                          {t.label}
                        </button>
                      ))}
                    </div>
                    {tabs.map((t) => {
                      if (t.key !== activeTab) return null;
                      if (t.lines) return <div key={t.key}>{renderLines()}</div>;
                      const f = fieldByName[t.field];
                      return (
                        <div key={t.key} className="o-notebook__page">
                          {renderControl(f, `o-${resource.key}-${f.name}`)}
                        </div>
                      );
                    })}
                  </div>
                )}

                {!isDraft && resource.footerFields && <div style={{ marginTop: 16 }}>{renderGroup([resource.footerFields])}</div>}
              </form>
            )}
          </div>
        </>
      )}
    </div>
  );
}
